'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2025);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    programs: [
      { label: 'Full Stack Development', href: '/java-full-stack-with-gen-ai' },
      { label: 'Gen-AI for Beginners', href: '/beginner-gen-ai' }
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Career Services', href: '/career-services' },
      { label: 'Success Stories', href: '/homepage' },
      { label: 'Contact', href: '/about' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/policies' },
      { label: 'Terms of Service', href: '/policies' },
      { label: 'Refund Policy', href: '/policies' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: '/assets/images/linkedin.png', href: 'https://www.linkedin.com/company/vitiquest' },
    { name: 'Twitter', icon: '/assets/images/x.png', href: 'https://www.x.com/vitiquest' },
    { name: 'facebook', icon: '/assets/images/fb.png', href: 'https://www.facebook.com/profile/vitiquest' },
    { name: 'Instagram', icon: '/assets/images/instagram.png', href: 'https://www.instagram.com/vitiquest' },
  ];

  return (
    <footer className={`bg-card border-t border-border ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 space-y-6">
            <Link href="/homepage" className="flex items-center space-x-3 group">
              <div className="relative w-10 h-10">
                <img src='/assets/images/ViTi.png' alt="VitiQuest logo" loading="lazy" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-headline font-bold text-foreground">VitiQuest</span>
                <span className="text-xs font-body text-muted-foreground -mt-1">Learning</span>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground font-body leading-relaxed max-w-xs">
              Transform your potential into expertise through practical, industry-aligned learning programs.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                  aria-label={social.name}
                >
                  <img src={social.icon} alt={social.name} loading="lazy" className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Programs Column */}
          <div className="space-y-4">
            <h3 className="text-sm font-headline font-bold text-foreground uppercase tracking-wide">Programs</h3>
            <ul className="space-y-3">
              {footerLinks.programs.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground font-body hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div className="space-y-4">
            <h3 className="text-sm font-headline font-bold text-foreground uppercase tracking-wide">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground font-body hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Column */}
          <div className="space-y-4">
            <h3 className="text-sm font-headline font-bold text-foreground uppercase tracking-wide">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground font-body hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground font-body text-center md:text-left">
              {isHydrated ? `© ${currentYear}` : '© 2025'} VitiQuest Learning. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground font-body">
                <Icon name="GlobeAltIcon" size={16} variant="outline" />
                <span>English (IN)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;